import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { MeResponse } from '@refdash/api-client';
import { api } from './api';
import { usePushNotifications } from '@/hooks/use-push-notifications';

interface AuthState {
  user: MeResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// Hospital-side roles the portal serves. Mirrors the backend's QUEUE_ROLES
// (delegation_service): reps and coordinators see the whole hospital,
// department heads and doctors see their department's queue.
const HOSPITAL_PORTAL_ROLES = new Set([
  'hospital_rep',
  'coordinator',
  'intake_coordinator',
  'department_head',
  'doctor',
]);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { registerToken, unregisterToken } = usePushNotifications();

  useEffect(() => {
    api.auth
      .me()
      .then((u) => {
        if (!HOSPITAL_PORTAL_ROLES.has(u.role)) {
          setUser(null);
        } else {
          setUser(u);
          registerToken();
        }
      })
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount;
  // registerToken is excluded intentionally — late permission grants are
  // handled via the Settings > Notifications page (requestPermission flow).
  }, []);

  // Re-validate the session whenever the tab regains focus, so a stale
  // PWA/background tab recovers (or shows login) immediately instead of
  // on the next poll. A valid request also slides the session cookie.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      api.auth.me().catch(() => setUser(null));
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.auth.login({ username, password });
    if (!HOSPITAL_PORTAL_ROLES.has(res.role)) {
      await api.auth.logout();
      throw new Error('Access denied. This portal is for hospital staff only.');
    }
    setUser({
      username: res.user,
      display_name: res.display_name,
      role: res.role,
      hospital_id: res.hospital_id,
      hospital_name: res.hospital_name ?? null,
      department_id: res.department_id ?? null,
      doctor_id: res.doctor_id ?? null,
    });
    registerToken();
  }, [registerToken]);

  const logout = useCallback(async () => {
    await unregisterToken();
    await api.auth.logout().catch(() => {});
    setUser(null);
  }, [unregisterToken]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
